// src/Components/Modals/AcceptDocumentsModal.tsx
import {
	FxIdDomainEntitiesDocumentsContentType,
	FxIdDomainEntitiesDocumentsDocument,
	FxIdDomainEntitiesDocumentsGameDocumentVersion
} from "@fxid/openapi-client";
import { queries } from "@fxid/openapi-client";
import { Anchor, Box, Button, Stack, Text, Title } from "@mantine/core";
import { ContextModalProps } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import log from "loglevel";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import snarkdown from "snarkdown";

import OpenApiClient from "../../../../packages/fxid-openapi-client/src/OpenApiClient.ts";

/**
 * Пропсы, которые модальное окно получает при открытии.
 */
export interface AcceptDocumentsModalInnerProps {
	/** Документ, который необходимо принять. */
	documentToAccept: FxIdDomainEntitiesDocumentsDocument;
	/** Последняя версия документа. */
	latestVersion: FxIdDomainEntitiesDocumentsGameDocumentVersion;
}

/**
 * Модальное окно для принятия юридических документов.
 * Отображает один конкретный документ, переданный через пропсы,
 * и предоставляет кнопку для его принятия.
 */
export const AcceptDocumentsModal = ({
	context,
	id,
	innerProps
}: ContextModalProps<AcceptDocumentsModalInnerProps>) => {
	const { documentToAccept, latestVersion } = innerProps;
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	// Мутация для принятия документа
	const { mutate: acceptDocument, isPending: isAccepting } = useMutation({
		mutationFn: (version: FxIdDomainEntitiesDocumentsGameDocumentVersion) =>
			OpenApiClient.Games.fxIdWebFeaturesDocumentsAcceptDocumentAcceptDocumentEndpoint({
				DocumentId: version.DocumentId,
				DocumentVersionId: version.Id
			}),
		onSuccess: () => {
			log.info("Document accepted successfully. Invalidating accepted documents query to refetch.");
			// Инвалидируем запрос, чтобы перезагрузить список принятых документов.
			void queryClient.invalidateQueries({ queryKey: queries.games.getUnacceptedDocuments._def });
			// Закрываем модальное окно после успешного принятия.
			context.closeModal(id);
		},
		onError: (error) => {
			log.error("Failed to accept document:", error);
			// Опционально: показать сообщение об ошибке пользователю
		}
	});

	const renderContent = () => {
		if (
			latestVersion.Content?.Value &&
			latestVersion.Content.ContentType === FxIdDomainEntitiesDocumentsContentType.Markdown
		) {
			const html = snarkdown(latestVersion.Content.Value);
			return <Box dangerouslySetInnerHTML={{ __html: html }} />;
		}

		return (
			<Text>
				<Trans
					i18nKey="accept_documents.document_info"
					values={{ documentType: documentToAccept.DocumentType.Value }}
					components={{
						anchor: <Anchor href={latestVersion.Url} target="_blank" rel="noopener noreferrer" />
					}}
					defaults="<anchor>Please read and following accept document: {{documentType}}</anchor>"
				/>
			</Text>
		);
	};

	return (
		<Stack style={{ display: "flex", flexDirection: "column", height: "100%" }}>
			<Title order={3}>{t("accept_documents.please_accept", "Please read and accept following document")}</Title>
			<Box style={{ flex: 1, overflowY: "auto", marginBlock: "1rem" }}>{renderContent()}</Box>
			<Button loading={isAccepting} onClick={() => acceptDocument(latestVersion)}>
				{t("accept_documents.accept_button", "Accept")}
			</Button>
		</Stack>
	);
};
